<script setup lang="ts">
import type { AgentContactCardEmits, AgentContactCardProps } from '.';

import { useContactForm } from '~/composables/useContactForm';
import { cn } from '~/utils';

import { BrokerAvatarInfo } from '~/components/BrokerAvatarInfo';
import { InputPhone } from '~/components/InputPhone';
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';

import IconMaterialSymbolsLightChatBubbleOutlineSharp from '~icons/material-symbols-light/chat-bubble-outline-sharp';

import messages from './AgentContactCard.locale.json';

const props = defineProps<AgentContactCardProps>();

const emit = defineEmits<AgentContactCardEmits>();

const strings = messages.pt;

const agentFirstName = computed(() => props.agentName?.split(' ')[0] ?? '');

const ctaText = computed(() =>
	strings.cta.replace('{agentFirstName}', agentFirstName.value)
);

const companyLine = computed(() =>
	props.companyName
		? strings.agentAt.replace('{companyName}', props.companyName)
		: ''
);

const initialUserMessage = computed(() =>
	strings.userMessageInitialValue.replace(
		'{agentFirstName}',
		agentFirstName.value
	)
);

const {
	name: nameValue,
	phone: phoneValue,
	userMessage: userMessageValue,
	errors,
	nameAttrs,
	phoneAttrs,
	userMessageAttrs,
	setFieldValue,
	isPhoneDisabled,
	handleSubmit: handleValidatedSubmit
} = useContactForm();

function useFieldModel(
	source: Readonly<Ref<string>>,
	field: 'name' | 'phone' | 'userMessage'
) {
	return computed({
		get: () => source.value,
		set: (value: string) => setFieldValue(field, value)
	});
}

const name = useFieldModel(nameValue, 'name');
const phone = useFieldModel(phoneValue, 'phone');
const userMessage = useFieldModel(userMessageValue, 'userMessage');
const handleSubmit = handleValidatedSubmit(() => {
	emit('submit', {
		name: name.value ?? '',
		phone: phone.value ?? '',
		userMessage: userMessage.value?.trim() || initialUserMessage.value
	});
});
</script>

<template>
	<section
		:class="
			cn(
				'flex min-h-[479px] w-full flex-col bg-secondary p-5 xl:p-7',
				props.class
			)
		"
		data-test-id="agent-contact-card"
	>
		<!--
			ml-9 (36px) no wrapper não é espaçamento estético: o badge da
			imobiliária usa `sizes="linear"` (left-2/3 + size-full), então ele
			transborda ~29px para fora da caixa do avatar de 44px. Menos que isso
			e o texto passa por baixo do badge. Sobram ~6px de folga visual.
		-->
		<BrokerAvatarInfo
			:border="false"
			avatar-shape="circle"
			company-avatar-shape="circle"
			:show-company-avatar="true"
			sizes="linear"
			:broker-src="agentPictureSrc || ''"
			:company-src="companyLogoSrc || ''"
			avatar-class="h-11 w-11"
			:info-classes="{
				wrapper: 'ml-9 min-w-0',
				brokerName:
					'text-primary font-sans text-base font-medium leading-[150%] truncate',
				companyName:
					'text-primary-light-2 font-sans text-sm font-normal leading-[150%] tracking-normal truncate'
			}"
			:broker-name="agentName"
			:company-name="companyName"
		>
			<template #broker>{{ agentName }}</template>
			<template #company>{{ companyLine }}</template>
		</BrokerAvatarInfo>

		<p class="pb-4 pt-6 text-base font-normal leading-[150%] text-primary">
			{{ strings.headline }}
		</p>

		<!-- ClientOnly para que dados já digitados não sejam capturados no cache de SSR -->
		<ClientOnly>
			<form
				id="agent-contact-form"
				class="flex flex-col gap-3"
				@submit.prevent="handleSubmit"
			>
				<Input
					v-model.trim="name"
					v-bind="nameAttrs"
					cy-test-id="agent-name-input"
					type="text"
					:error="errors.name"
					:placeholder="strings.namePlaceholder"
					class="h-[3.125rem] bg-white text-primary"
				/>

				<InputPhone
					v-model.trim="phone"
					v-bind="phoneAttrs"
					:error="errors.phone"
					:disabled="!!isPhoneDisabled"
					cy-test-id="agent-phone-input"
					class="bg-white text-primary"
				/>

				<Textarea
					v-model.trim="userMessage"
					v-bind="userMessageAttrs"
					data-testid="agent-user-message-textarea"
					:placeholder="initialUserMessage"
					:error="errors.userMessage"
					class="min-h-[5.625rem] resize-none bg-white text-primary"
				/>
			</form>

			<template #fallback>
				<form class="flex flex-col gap-3">
					<Input
						:placeholder="strings.namePlaceholder"
						class="h-[3.125rem] bg-white text-primary"
					/>
					<InputPhone model-value="" class="bg-white text-primary" />
					<Textarea
						:placeholder="initialUserMessage"
						class="min-h-[5.625rem] resize-none bg-white text-primary"
					/>
				</form>
			</template>
		</ClientOnly>

		<Button
			size="md"
			data-test-id="agent-contact-button"
			class="mt-6 flex h-[3.125rem] w-full items-center gap-3 text-base font-medium"
			type="submit"
			form="agent-contact-form"
		>
			<IconMaterialSymbolsLightChatBubbleOutlineSharp
				class="size-6 text-beige"
			/>
			{{ ctaText }}
		</Button>
	</section>
</template>
